import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Grid,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Stack,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import SQLLayout from "../layout/SQLLayout";
import ObjectExplorer from "../sidebar/ObjectExplorer";
import { databaseAPI } from "../../services/api";

export default function TableDataView() {
  const { schema, tableName } = useParams();

  // ===== Hooks (always before any return) =====
  const [sidebarSearch, setSidebarSearch] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [orderBy, setOrderBy] = useState("ID");
  const [orderDir, setOrderDir] = useState("ASC");
  const [searchQuery, setSearchQuery] = useState("");

  const dbQuery = useQuery({
    queryKey: ["databaseInfo"],
    queryFn: () => databaseAPI.getAllTables().then((r) => r.data),
    refetchOnWindowFocus: false,
  });

  const dataQuery = useQuery({
    queryKey: ["tableData", schema, tableName, page, pageSize, orderBy, orderDir],
    queryFn: () =>
      databaseAPI
        .getTableData(schema, tableName, { page, pageSize, orderBy, orderDir })
        .then((r) => r.data),
    refetchOnWindowFocus: false,
  });

  const db = dbQuery.data;
  const tableData = dataQuery.data;

  const columns = tableData?.columns ?? [];
  const rows = tableData?.data ?? [];
  const pagination = tableData?.pagination ?? {};
  const totalPages = pagination?.totalPages ?? 1;

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      columns.some((c) => String(r?.[c] ?? "").toLowerCase().includes(q))
    );
  }, [rows, columns, searchQuery]);

  // ===== Handlers =====
  const onRefresh = async () => {
    await Promise.all([dbQuery.refetch(), dataQuery.refetch()]);
    toast.success("Refreshed");
  };

  const onBackup = async () => {
    try {
      const res = await databaseAPI.startBackup();
      const file = res.data?.file;
      if (!file) throw new Error("No file");
      window.location.href = databaseAPI.getBackupDownloadUrl(file);
      toast.success("Backup created");
    } catch {
      toast.error("Backup failed (backend required)");
    }
  };

  const formatValue = (v) => {
    if (v === null || v === undefined)
      return <Chip size="small" label="NULL" variant="outlined" />;
    if (typeof v === "boolean") return v ? "1" : "0";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  const handleSort = (col) => {
    if (orderBy === col) setOrderDir(orderDir === "ASC" ? "DESC" : "ASC");
    else {
      setOrderBy(col);
      setOrderDir("ASC");
    }
    setPage(1);
  };

  // ===== Rendering states AFTER hooks =====
  if (dbQuery.isLoading || dataQuery.isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (dbQuery.error || dataQuery.error) {
    const msg = (dbQuery.error || dataQuery.error)?.message || "Unknown error";
    return (
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 2 }}>
          <Typography color="error">Error: {msg}</Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <SQLLayout
      title={`${db?.database ?? "Database"} — ${schema}.${tableName} (Data)`}
      subtitle={`Server: ${db?.server ?? "-"} | Page ${page} / ${totalPages}`}
      onRefresh={onRefresh}
      onBackup={onBackup}
      sidebarSearch={sidebarSearch}
      setSidebarSearch={setSidebarSearch}
      sidebar={<ObjectExplorer tables={db?.tables ?? []} filter={sidebarSearch} />}
    >
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {schema}.{tableName}
          </Typography>
          <Chip
            label={`${pagination?.totalCount?.toLocaleString?.() ?? "-"} rows`}
            variant="outlined"
          />
          <Chip label={`${columns.length} cols`} variant="outlined" />
          <Box sx={{ flex: 1 }} />
          <Chip
            label={`ORDER: ${orderBy} ${orderDir}`}
            sx={{ fontFamily: "ui-monospace, Menlo, monospace" }}
            variant="outlined"
          />
        </Stack>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search (current page only)…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Order By"
              value={orderBy}
              onChange={(e) => {
                setOrderBy(e.target.value);
                setPage(1);
              }}
            >
              {columns.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Direction"
              value={orderDir}
              onChange={(e) => {
                setOrderDir(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="ASC">ASC</MenuItem>
              <MenuItem value="DESC">DESC</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              label="Page Size"
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(1);
              }}
            >
              {[10, 25, 50, 100, 200, 500].map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ maxHeight: 680 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell
                  key={c}
                  onClick={() => handleSort(c)}
                  sx={{
                    cursor: "pointer",
                    userSelect: "none",
                    fontFamily: "ui-monospace, Menlo, monospace",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{c}</span>
                    {orderBy === c && (
                      <Chip size="small" label={orderDir} variant="outlined" sx={{ height: 22 }} />
                    )}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredRows.map((row, idx) => (
              <TableRow key={idx} hover>
                {columns.map((c) => (
                  <TableCell
                    key={c}
                    sx={{
                      fontFamily: "ui-monospace, Menlo, monospace",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatValue(row?.[c])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Paper sx={{ p: 2, mt: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredRows.length} rows (current page) — Total:{" "}
            {pagination?.totalCount?.toLocaleString?.() ?? "-"}
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, p) => setPage(p)}
            showFirstButton
            showLastButton
            size="large"
          />

          <Typography variant="body2" color="text.secondary">
            Page {page} / {totalPages}
          </Typography>
        </Stack>
      </Paper>
    </SQLLayout>
  );
}
